import React, { useState, useEffect } from 'react';
import { Save } from 'lucide-react';
import { validators } from '../../utils/validators';
import { formatters } from '../../utils/formatters';

const CompanyForm = ({ company, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    isWhatsApp: false,
    address: '',
    temporaryPassword: '',
    active: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        isWhatsApp: company.isWhatsApp || false,
        address: formatters.formatAddress(company.address),
        temporaryPassword: '',
        active: company.active !== undefined ? company.active : true
      });
    }
  }, [company]);

  const validateForm = () => {
    const newErrors = {};

    if (!validators.isValidCompanyName(formData.name)) {
      newErrors.name = 'Nome da empresa deve ter pelo menos 2 caracteres';
    }

    if (!validators.isValidEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.phone || formData.phone.trim().length < 10) {
      newErrors.phone = 'Telefone é obrigatório e deve ter pelo menos 10 dígitos';
    }

    if (!company && (!formData.temporaryPassword || formData.temporaryPassword.length < 6)) {
      newErrors.temporaryPassword = 'Senha provisória deve ter pelo menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSubmit(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome da Empresa *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.name ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Digite o nome da empresa"
        />
        {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email *
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.email ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="Digite o email da empresa"
        />
        {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Telefone *
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            errors.phone ? 'border-red-300' : 'border-gray-300'
          }`}
          placeholder="(11) 99999-9999"
        />
        {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone}</p>}
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="isWhatsApp"
          checked={formData.isWhatsApp}
          onChange={(e) => setFormData({ ...formData, isWhatsApp: e.target.checked })}
          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
        />
        <label htmlFor="isWhatsApp" className="ml-2 text-sm text-gray-700">
          Este número é WhatsApp
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Endereço
        </label>
        <textarea
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Endereço completo da empresa (opcional)"
          rows="3"
        />
        <p className="text-sm text-gray-500 mt-1">
          Campo opcional - pode ser preenchido posteriormente
        </p>
      </div>

      {!company && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Senha Provisória *
          </label>
          <input
            type="password"
            value={formData.temporaryPassword}
            onChange={(e) => setFormData({ ...formData, temporaryPassword: e.target.value })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.temporaryPassword ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Digite uma senha provisória (mín. 6 caracteres)"
          />
          {errors.temporaryPassword && <p className="text-sm text-red-600 mt-1">{errors.temporaryPassword}</p>}
          <p className="text-sm text-gray-500 mt-1">
            A empresa precisará alterar esta senha no primeiro login
          </p>
        </div>
      )}

      <div className="flex items-center">
        <input
          type="checkbox"
          id="active"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="active" className="ml-2 text-sm text-gray-700">
          Empresa ativa
        </label>
      </div>

      <div className="flex space-x-3 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{company ? 'Atualizar' : 'Criar'}</span>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;